import React, { useRef } from 'react';
import { Drawer } from 'vaul';
import { X } from 'lucide-react';
import { useAppTranslations } from '@/hooks/useAppTranslations';
import { useAfterClose } from '@/hooks/useAfterClose';

/**
 * The one bottom sheet every detail view uses: rises from the bottom, follows
 * the finger when dragged (grabber or content scrolled to the top), flicks
 * away, and slides back out the way it came in.
 *
 * Portalled to <body> above both bars (z-60 vs z-50), so the bars stay visible
 * under the dim instead of disappearing; it never sets data-modal-open.
 */

// How long vaul's exit takes; onClosed fires after it so content can be cleared.
const EXIT_MS = 500;

export const IOSSheetClose = Drawer.Close;

interface IOSSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Always announced to VoiceOver; shown in the default header row. */
  title: string;
  hideTitle?: boolean;
  /** Replaces the default title row (the title is then screen-reader only). */
  header?: React.ReactNode;
  /** Pinned under the scrolling content, above the home indicator. */
  footer?: React.ReactNode;
  dismissible?: boolean;
  maxHeight?: string;
  /** Sheet surface colour (default white). */
  background?: string;
  /** Called once the sheet has finished sliding away. */
  onClosed?: () => void;
  children?: React.ReactNode;
}

export function IOSSheet({
  open,
  onOpenChange,
  title,
  hideTitle,
  header,
  footer,
  dismissible = true,
  maxHeight = '88vh',
  background = '#FFFFFF',
  onClosed,
  children,
}: IOSSheetProps) {
  const { t } = useAppTranslations();

  // vaul's onAnimationEnd only fires for closes vaul starts itself (drag,
  // scrim, Escape), not when the parent sets open=false, so time it here.
  useAfterClose(open, onClosed, EXIT_MS);
  const contentRef = useRef<HTMLDivElement>(null);

  return (
    <Drawer.Root open={open} onOpenChange={onOpenChange} dismissible={dismissible} shouldScaleBackground={false}>
      <Drawer.Portal>
        <Drawer.Overlay
          className="fixed inset-0 z-[60] bg-black/40"
          style={{ backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)' }}
        />
        <Drawer.Content
          ref={contentRef}
          aria-describedby={undefined}
          // Move focus into the sheet so VoiceOver announces it (vaul leaves it on
          // the trigger, which Radix has just hidden). The sheet itself, not its
          // first field, so the keyboard doesn't pop up.
          onOpenAutoFocus={(e) => {
            e.preventDefault();
            contentRef.current?.focus({ preventScroll: true });
          }}
          className="fixed inset-x-0 bottom-0 z-[60] mx-auto flex w-full max-w-lg flex-col rounded-t-[28px] outline-none"
          style={{ maxHeight, background, boxShadow: '0 -8px 40px rgba(15, 23, 42, 0.18)' }}
        >
          {/* Grabber: says "this can be dragged down". preventCycle stops a tap on it closing the sheet. */}
          <Drawer.Handle
            preventCycle
            className="!mt-2.5 !mb-1 !h-[5px] !w-9 !rounded-full !bg-slate-300 !opacity-100"
          />

          {header ? (
            <>
              <Drawer.Title className="sr-only">{title}</Drawer.Title>
              {header}
            </>
          ) : (
            <div className="flex items-center gap-3 px-4 pb-2 pt-1">
              <Drawer.Title
                className={hideTitle ? 'sr-only' : 'flex-1 text-[17px] font-bold tracking-tight text-slate-900'}
              >
                {title}
              </Drawer.Title>
              {dismissible && (
                <Drawer.Close asChild>
                  <button
                    type="button"
                    aria-label={t.common.close}
                    className="tap-target ml-auto flex h-9 w-9 items-center justify-center rounded-xl bg-slate-100 text-slate-600 transition-transform duration-100 active:scale-95"
                  >
                    <X className="h-[18px] w-[18px]" />
                  </button>
                </Drawer.Close>
              )}
            </div>
          )}

          <div
            className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4"
            style={{ paddingBottom: footer ? 12 : 'max(16px, env(safe-area-inset-bottom))' }}
          >
            {children}
          </div>

          {footer ? (
            <div className="px-4 pt-2" style={{ paddingBottom: 'max(12px, env(safe-area-inset-bottom))' }}>
              {footer}
            </div>
          ) : null}
        </Drawer.Content>
      </Drawer.Portal>
    </Drawer.Root>
  );
}

export default IOSSheet;
